import React, { useMemo } from 'react';

import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts/types';
import { useCompanyHub } from '@atlassian/global-shortcuts/company-hub';
import { useJira } from '@atlassian/global-shortcuts/jira';
import { useLoom } from '@atlassian/global-shortcuts/loom';
import { useTeams } from '@atlassian/global-shortcuts/teams';
import type { CustomItemProps } from '@atlassian/navigation-common/types';

import { CompanyHubItem } from '../apps/CompanyHubItem/CompanyHubItem.tsx';

import { useShortcutVisibilityOverrides } from './useShortcutVisibilityOverrides.tsx';
import { useFallbackUrls } from './useFallbackUrls.tsx';
import { useJpdShortcutItemsConfig } from './useJpdShortcutItemsConfig.tsx';
import { useLicensedProducts } from './useLicensedProducts.tsx';

export type ShortcutItemsConfigProps = {
	teamsShortcutConfig?: GlobalShortcutsItem;
};

export const useShortcutItemsConfig = (
	props?: ShortcutItemsConfigProps,
): Record<string, GlobalShortcutsItem> => {
	const fallbackUrls = useFallbackUrls();
	const { hasLoom, hasJira, hasJpd } = useLicensedProducts();
	const { isPeopleVisible, isCompanyHubVisible, isJpdVisible } = useShortcutVisibilityOverrides();

	const companyHubShortcutWithoutComponent = useCompanyHub(fallbackUrls['companyHubShortcut']);
	const jiraShortcut = useJira('');
	const loomShortcut = useLoom('');
	const teamsShortcut = useTeams(fallbackUrls['teamsShortcut']);
	const jpdShortcutItemsConfig = useJpdShortcutItemsConfig(hasJpd);

	return useMemo(() => {
		const companyHubShortcut: GlobalShortcutsItem = {
			...companyHubShortcutWithoutComponent,
			menuItemId: 'companyHubShortcut',
			type: 'component',
			isReorderable: true,
			component: (itemProps: CustomItemProps) => <CompanyHubItem {...itemProps} />,
		};

		return {
			...(isCompanyHubVisible ? { companyHubShortcut } : {}),
			...(hasJira ? { jiraShortcut } : {}),
			...(hasLoom ? { loomShortcut } : {}),
			...(isJpdVisible ? jpdShortcutItemsConfig : {}),
			...(isPeopleVisible
				? {
						teamsShortcut: props?.teamsShortcutConfig ?? teamsShortcut,
					}
				: {}),
		};
	}, [
		companyHubShortcutWithoutComponent,
		hasJira,
		jiraShortcut,
		hasLoom,
		loomShortcut,
		jpdShortcutItemsConfig,
		teamsShortcut,
		props?.teamsShortcutConfig,
		isCompanyHubVisible,
		isJpdVisible,
		isPeopleVisible,
	]);
};
